import React, {useState } from 'react';
import ReactDOM from 'react-dom';
import { Play, Check, XCircle, X } from 'lucide-react';
import '../../ModuleInfoModal.css';

const ModuleInfoModal = ({ 
  moduleData,
  infoTitle,
  infoMessages,
  isFirstMessageReady,
  onFetchQuestions,
  questions,
  isLoadingQuestions,
  onClose,
  onBeginDialogue
}) => {
  const hasVideo = !!moduleData?.video_filename;

  // Quiz states (similar to IntroQuiz)
  const [showVideo, setShowVideo] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleVideoClick = async () => {
    setShowVideo(true);
    // Fetch questions when video is clicked
    if (onFetchQuestions) {
      await onFetchQuestions();
    }
  };

  const handleStartQuiz = () => {
    setShowVideo(false);
    setShowQuiz(true);
  };

  const handleAnswerChange = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitQuiz = () => {
    setSubmitted(true);
  };

  const handleCloseQuiz = () => {
    setShowQuiz(false);
    setShowVideo(false);
    setUserAnswers({});
    setSubmitted(false);
  };

  // Check if all questions are answered
  const allQuestionsAnswered = questions?.length > 0 && 
    Object.keys(userAnswers).length === questions.length;

  const modalContent = (
    <div className="modal-overlay-module-info">
      <div className="modal-quiz-wrapper">
        
        {/* Video Section - shown when video is clicked */}
        {showVideo && hasVideo && !showQuiz && (
          <>
            <div className="video-container-quiz">
              <video 
                controls 
                className="video-player-quiz"
                key={moduleData.video_filename}
              >
                <source src={moduleData.video_filename} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="video-footer-quiz">
              {isLoadingQuestions ? (
                <div className="centered-loading">
                  <span className="loading-spinner medium"></span>
                  <p className="loading-text">Loading quiz...</p>
                </div>
              ) : questions && questions.length > 0 ? (
                <button onClick={handleStartQuiz} className="btn-start-quiz">
                  Start Quiz
                </button>
              ) : (
                <button onClick={handleCloseQuiz} className="btn-start-quiz">
                  Continue
                </button>
              )}
            </div>
          </>
        )}

        {/* Quiz Section - shown after "Start Quiz" is clicked */}
        {showQuiz && (
          <div className="modal-quiz">
            <div className="modal-header-quiz">
              <h2 className="modal-title-quiz">Module Quiz</h2>
            </div>

            <div className="modal-content-quiz">
              <div className="questions-container-quiz">
                {questions?.map((q, idx) => (
                  <div key={q.question_id} className="question-block-quiz">
                    <h3 className="question-text-quiz">
                      {idx + 1}. {q.question}
                    </h3>
                    
                    <div className="choices-container-quiz">
                      {['choice_a', 'choice_b', 'choice_c', 'choice_d'].map((choice) => {
                        const choiceLetter = choice.split('_')[1].toUpperCase();
                        const isSelected = userAnswers[q.question_id] === choiceLetter;
                        const isCorrect = q.correct_answer === choiceLetter;
                        const showResult = submitted;
                        
                        let className = 'choice-item-quiz';
                        if (showResult) {
                          if (isCorrect) {
                            className += ' choice-correct-quiz';
                          } else if (isSelected && !isCorrect) {
                            className += ' choice-incorrect-quiz';
                          }
                        } else if (isSelected) {
                          className += ' choice-selected-quiz';
                        }

                        return (
                          <label key={choice} className={className}>
                            <input
                              type="radio"
                              name={`question-${q.question_id}`}
                              value={choiceLetter}
                              checked={isSelected}
                              onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                              disabled={submitted}
                              className="choice-radio-quiz"
                            />
                            <span className="choice-content-quiz">
                              <span className="choice-text-quiz">
                                <strong>{choiceLetter}.</strong> {q[choice]}
                              </span>
                              {showResult && isCorrect && (
                                <span className="choice-feedback-correct-quiz">
                                  <Check size={18} />
                                  Correct
                                </span>
                              )}
                              {showResult && isSelected && !isCorrect && (
                                <span className="choice-feedback-incorrect-quiz">
                                  <XCircle size={18} />
                                  Incorrect
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer-quiz">
              {!submitted ? (
                <button
                  onClick={handleSubmitQuiz}
                  disabled={!allQuestionsAnswered}
                  className={`btn-submit-quiz ${!allQuestionsAnswered ? 'btn-disabled-quiz' : ''}`}
                >
                  Submit Answers
                </button>
              ) : (
                <button onClick={handleCloseQuiz} className="btn-submit-quiz">
                  Close
                </button>
              )}
            </div>
          </div>
        )}

        {/* Main Info View - default view */}
        {!showVideo && !showQuiz && (
          <div className="modal-module-info">
            {/* 1. Loading/Begin Module Status - FIRST */}
            <div className="module-status-row">
              {!isFirstMessageReady ? (
                <div className="centered-loading">
                  <span className="loading-spinner medium"></span>
                  <p className="loading-text">Loading tutor...</p>
                </div>
              ) : (
                <div className="centered-button-container">
                  <button className="start-button" onClick={onBeginDialogue}>
                    Begin Dialogue
                  </button>
                </div>
              )}
            </div>

            {/* 2. Info Title - SECOND, Centered */}
            <h2 className="module-info-title">{infoTitle}</h2>

            {/* 3. Info Messages - THIRD, Bulleted list, left-aligned */}
            {infoMessages.length > 0 && (
              <ul className="module-info-messages">
                {infoMessages
                  .sort((a, b) => a.detail_seq - b.detail_seq)
                  .map((msg, idx) => (
                    <li key={idx} className="module-info-message-item">
                      {msg.detail_title}
                    </li>
                  ))}
              </ul>
            )}

            {/* 4. Video Thumbnail Section - FOURTH */}
            {hasVideo && (
              <div className="video-preview-section">
                <div className="video-preview-container" onClick={handleVideoClick}>
                  <div className="video-thumbnail">
                    <video
                      className="video-thumbnail-element"
                      src={moduleData.video_filename}
                      preload="metadata"
                    />
                    <div className="video-play-overlay">
                      <Play size={64} className="play-icon" />
                    </div>
                  </div>
                  <p className="video-preview-text">Learn the module's key concepts by watching the above video before continuing</p>
                </div>
              </div>
            )}

            {/* 5. Start Quiz Button - FIFTH (only if questions exist and video not playing) */}
            {questions && questions.length > 0 && (
              <div className="quiz-button-section">
                <button 
                  onClick={() => setShowQuiz(true)} 
                  className="btn-start-quiz-main"
                >
                  Start Quiz
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ModuleInfoModal;